import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button } from '@/components/ui';

export function UploadDialog({
  folderId,
  onClose,
  onUploaded,
}: {
  folderId: string | null;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: async (selected: File) => {
      const session = await filesApi.requestUpload({
        fileName: selected.name,
        mimeType: selected.type || 'application/octet-stream',
        declaredSize: selected.size,
        folderId,
      });
      await filesApi.putBytes(session.uploadUrl, selected);
      return filesApi.completeUpload(session.id, { fileName: selected.name, folderId });
    },
    onSuccess: onUploaded,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="upload-file-title"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          if (file) mutation.mutate(file);
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="upload-file-title">{t('files.file.upload')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="field">
          <label className="field-label" htmlFor="file-input">
            {t('files.upload.selectFile')}
          </label>
          <input
            id="file-input"
            type="file"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={!file} loading={mutation.isPending}>
            {mutation.isPending ? t('files.upload.uploading') : t('files.file.upload')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
